import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import Modal from "@mui/material/Modal/Modal";
import Tooltip from "@mui/material/Tooltip";
import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DownloadFileProp } from "../../api";
import ExceptionDialog from "../../components/errorDialog";
import { Spinner } from "../../components/spinner";
import { handlesOwnKeys } from "../../utils/keyboardTarget";
import { isMediaControlsTouch } from "../../utils/mediaTouch";
import FilePreview from "./filePreview";

/** Server-sent error surfaced by a preview (localized via the error bundle). */
type ServerError = { title: string; description: string };

/**
 * Prev/next paging for the overlay. The parent owns which file is open;
 * `onNavigate` asks it to move to a neighbor (it responds by swapping the
 * per-file props). Omit when there is nothing to page through.
 */
export type PreviewNavigation = {
  hasPrevious: boolean;
  hasNext: boolean;
  onNavigate: (direction: 1 | -1) => void;
};

type PreviewPageProp = DownloadFileProp & {
  onClose: () => void;
  filePreviewUrl?: URL;
  fileDownloadUrl?: string;
  mimeType?: string;
  /** Natural width/height ratio of the file, from the share-link metadata. */
  aspectRatio?: number;
  /**
   * Optional auth refresh awaited before the "no preview" fallback download.
   * Provided in the share-link context; absent for transfers.
   */
  refreshAuth?: () => Promise<void>;
  /**
   * Optional bearer-token supplier, forwarded to the preview fetches so a guest
   * whose cookie is blocked cross-site (published space in dev) can still load
   * previews via an `Authorization` header.
   */
  getAuthToken?: () => string | null | undefined;
  /** Direct file URL for the transfer fallback download (absent for share-links). */
  fileUrl?: URL;
  /** Prev/next paging (see {@link PreviewNavigation}); omit for a lone file. */
  navigation?: PreviewNavigation;
};

/**
 * Largest fraction of the viewport the preview may occupy, leaving a
 * comfortable margin on every side so it never touches the screen edge.
 */
const PREVIEW_MAX_VW = 90;
const PREVIEW_MAX_VH = 90;

/** Bounds the preview endpoint accepts for width/height (100 < n < 5000). */
const PREVIEW_REQUEST_MIN_PX = 100;
const PREVIEW_REQUEST_MAX_PX = 5000;

/**
 * Pixel size to request for the modal preview image. The endpoint's
 * width/height are a *max box* (aspect preserved, always JPEG), so we request
 * the on-screen frame's box — {@link PREVIEW_MAX_VW}vw × {@link PREVIEW_MAX_VH}
 * vh — scaled by devicePixelRatio and clamped to the endpoint's bounds. The
 * server fits the image's aspect within that box, so the result is the actual
 * display size at native resolution: sharper on hi-dpi/large screens, less
 * over-fetch on small ones, versus a fixed size. The ETag is keyed per
 * (width,height), so each size still caches cleanly.
 */
export function previewImageRequestSize(): { width: number; height: number } {
  const dpr = window.devicePixelRatio || 1;
  const clampPx = (n: number) =>
    Math.round(
      Math.min(PREVIEW_REQUEST_MAX_PX, Math.max(PREVIEW_REQUEST_MIN_PX, n))
    );
  return {
    width: clampPx((PREVIEW_MAX_VW / 100) * window.innerWidth * dpr),
    height: clampPx((PREVIEW_MAX_VH / 100) * window.innerHeight * dpr),
  };
}

/**
 * Aspect-ratio (width / height) clamp for the preview frame. Every common shape
 * — 16:9 (1.78), 3:2 (1.5), 4:3, square, A4 portrait (0.71), 9:16 (0.56) — sits
 * inside this range and renders at its true ratio. Only extreme panoramas /
 * tall strips are clamped: they'd otherwise fit as an unreadable sliver, so we
 * cap the frame and let the image sit matted (centered) within it instead.
 */
const PREVIEW_MAX_ASPECT = 2; // 2:1 — widest frame
const PREVIEW_MIN_ASPECT = 0.5; // 1:2 — tallest frame

/** A4 portrait (1 / √2 ≈ 0.707) — default PDF page ratio when none is given. */
const PDF_FALLBACK_ASPECT = 1 / Math.SQRT2;

/**
 * Size the preview frame to the file's natural aspect ratio, scaled to the
 * largest box that fits within {@link PREVIEW_MAX_VW}×{@link PREVIEW_MAX_VH}
 * with padding. `min(maxW, maxH * ar)` picks the larger dimension that still
 * keeps the other within bounds; `aspect-ratio` derives the rest. PDFs are
 * ratio-sized too (so the viewer's dark backdrop hugs the page instead of
 * leaving oversized bars), defaulting to A4 portrait when the metadata has no
 * ratio. Audio has no meaningful ratio, so it gets a compact fixed box.
 */
function previewBoxStyle(
  mimeType: string | undefined,
  aspectRatio: number | undefined,
  imageNaturalSize: { width: number; height: number } | null
): CSSProperties {
  const type = mimeType?.split("/")[0];
  const isPdf = mimeType?.endsWith("pdf") ?? false;

  // Once an image has loaded, hug its natural size (capped to the viewport box)
  // so a small image shows at 1:1 instead of being upscaled to fill the frame,
  // while a large one still shrinks to fit. The endpoint never upscales (it
  // caps at the source), so naturalWidth/Height is the true source size. Keyed
  // on the measured size (not the declared mime) so a server-rendered preview
  // image of a non-image file — e.g. a transfer's CSV/text — hugs it too.
  if (
    imageNaturalSize &&
    imageNaturalSize.width > 0 &&
    imageNaturalSize.height > 0
  ) {
    const ar = imageNaturalSize.width / imageNaturalSize.height;
    return {
      width: `min(${imageNaturalSize.width}px, ${PREVIEW_MAX_VW}vw, calc(${PREVIEW_MAX_VH}vh * ${ar}))`,
      maxHeight: `${PREVIEW_MAX_VH}vh`,
      aspectRatio: `${ar}`,
    };
  }

  if (type === "image" || type === "video" || isPdf) {
    const rawAspect =
      aspectRatio && aspectRatio > 0
        ? aspectRatio
        : isPdf
        ? PDF_FALLBACK_ASPECT
        : undefined;

    if (rawAspect) {
      const ar = Math.min(
        PREVIEW_MAX_ASPECT,
        Math.max(PREVIEW_MIN_ASPECT, rawAspect)
      );
      return {
        width: `min(${PREVIEW_MAX_VW}vw, calc(${PREVIEW_MAX_VH}vh * ${ar}))`,
        maxWidth: `${PREVIEW_MAX_VW}vw`,
        maxHeight: `${PREVIEW_MAX_VH}vh`,
        aspectRatio: `${ar}`,
      };
    }
    // Unknown ratio: a generous box; object-contain still shows the whole image.
    return { width: `${PREVIEW_MAX_VW}vw`, height: `${PREVIEW_MAX_VH}vh` };
  }

  if (type === "audio") {
    return { width: "min(90vw, 480px)" };
  }

  // Fallback / unknown: size to content (e.g. the "no preview" card).
  return {};
}

// ==========================================================================
//                        Prev/next slot choreography
// ==========================================================================

/**
 * The per-file props, snapshotted per slot so the outgoing file keeps
 * rendering (from its own snapshot) while its successor loads.
 */
type PreviewEntry = {
  objectUid: string;
  fileName: string;
  mimeType?: string;
  aspectRatio?: number;
  filePreviewUrl?: URL;
  fileDownloadUrl?: string;
  fileUrl?: URL;
};

/**
 * A slot's lifecycle when paging between files:
 *  - `"pending"`: mounted invisibly so it can load; the previous file stays
 *    visible meanwhile, so paging never blanks to a spinner.
 *  - `"shown"`:   the (single) visible file; promoted from pending the moment
 *    it can render (loaded, or its fallback card).
 *  - `"leaving"`: the displaced file, sliding out; unmounted afterwards.
 */
type SlotState = "pending" | "shown" | "leaving";
type PreviewSlotData = {
  /** Unique per mounted instance, so a slot re-added while its predecessor is
   *  still `leaving` gets a fresh key and remounts (rather than reconciling to
   *  the stale instance, which would never re-fire its load callbacks). */
  id: number;
  entry: PreviewEntry;
  state: SlotState;
  /** Paging direction (1 = next), fixing which side the slot enters/exits. */
  direction: 1 | -1;
};

/** Duration of the prev/next hand-off (transform; opacity runs slightly shorter). */
const NAV_TRANSITION_MS = 240;
const NAV_FADE_MS = 200;
/** Horizontal travel of the hand-off. */
const NAV_SLIDE_PX = 40;
/** Minimum horizontal travel (px) of a touch swipe that pages to a neighbor. */
const SWIPE_MIN_PX = 45;
/**
 * Window (ms) after a paging swipe in which a click is taken to be the tap the
 * browser synthesizes from that swipe, rather than a deliberate one.
 */
const CLICK_AFTER_SWIPE_MS = 400;

/** Fade-only paging for users who prefer reduced motion. */
function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export default function PreviewPage(previewProp: PreviewPageProp) {
  const { t } = useTranslation();
  // A preview that fails with a server error worth surfacing — a 429 (rate
  // limit) or a 401 whose silent token refresh failed — shows the error dialog
  // (server's message, localized via the bundle) instead of the fallback card.
  const [serverError, setServerError] = useState<ServerError | null>(null);

  // Latest props for the stable callbacks/effects below (paging swaps the
  // per-file props without remounting this component).
  const propsRef = useRef(previewProp);
  propsRef.current = previewProp;
  const snapshotEntry = (): PreviewEntry => {
    const p = propsRef.current;
    return {
      objectUid: p.objectUid,
      fileName: p.fileName,
      mimeType: p.mimeType,
      aspectRatio: p.aspectRatio,
      filePreviewUrl: p.filePreviewUrl,
      fileDownloadUrl: p.fileDownloadUrl,
      fileUrl: p.fileUrl,
    };
  };

  const slotIdRef = useRef(0);
  const [slots, setSlots] = useState<PreviewSlotData[]>(() => [
    {
      id: slotIdRef.current,
      entry: snapshotEntry(),
      state: "shown",
      direction: 1,
    },
  ]);
  // Mirror for the server-error handler, which branches on the erroring slot's
  // current state without re-creating its identity.
  const slotsRef = useRef(slots);
  slotsRef.current = slots;
  // Direction of the latest navigation, stamped onto the slot it creates.
  const navDirectionRef = useRef<1 | -1>(1);
  const exitTimerRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(exitTimerRef.current), []);

  // When the parent points at a new file (prev/next), mount it as a hidden
  // "pending" slot. Rapid paging replaces the pending slot with the newest
  // target; the shown file always stays until a successor is ready.
  useEffect(() => {
    const uid = previewProp.objectUid;
    setSlots((prev) => {
      const existing = prev.find((s) => s.entry.objectUid === uid);
      if (existing && existing.state !== "leaving") {
        // Already the shown (or pending) slot, e.g. paged forward and straight
        // back before the neighbor was ready. Drop any *other* pending slot so
        // it can't finish loading later and steal the view.
        return prev.some((s) => s.state === "pending" && s !== existing)
          ? prev.filter((s) => s.state !== "pending" || s === existing)
          : prev;
      }
      return [
        ...prev.filter(
          (s) => s.state !== "pending" && s.entry.objectUid !== uid
        ),
        {
          id: ++slotIdRef.current,
          entry: snapshotEntry(),
          state: "pending",
          direction: navDirectionRef.current,
        },
      ];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewProp.objectUid]);

  // Promote a pending slot the moment its file is displayable: it slides in as
  // the current file (stamped with the same direction) slides out.
  const promoteSlot = useCallback((uid: string) => {
    setSlots((prev) => {
      const pending = prev.find(
        (s) => s.state === "pending" && s.entry.objectUid === uid
      );
      if (!pending) return prev;
      return prev.map((s) => {
        if (s === pending) return { ...s, state: "shown" as const };
        if (s.state === "shown")
          return {
            ...s,
            state: "leaving" as const,
            direction: pending.direction,
          };
        return s;
      });
    });
    window.clearTimeout(exitTimerRef.current);
    exitTimerRef.current = window.setTimeout(() => {
      setSlots((prev) =>
        prev.some((s) => s.state === "leaving")
          ? prev.filter((s) => s.state !== "leaving")
          : prev
      );
    }, NAV_TRANSITION_MS);
  }, []);

  // A server error (429, or a 401 whose refresh failed) tears the overlay down
  // to the dialog only when it comes from the *shown* file. A background
  // neighbor being preloaded (pending/leaving) must never discard the file the
  // user is looking at, so its failed slot is just dropped.
  const handleSlotServerError = useCallback(
    (id: number, error: ServerError) => {
      const slot = slotsRef.current.find((s) => s.id === id);
      if (!slot || slot.state === "shown") {
        setServerError(error);
        return;
      }
      setSlots((prev) => prev.filter((s) => s.id !== id));
    },
    []
  );

  const navigate = useCallback((direction: 1 | -1) => {
    const nav = propsRef.current.navigation;
    if (!nav) return;
    if (direction === 1 && !nav.hasNext) return;
    if (direction === -1 && !nav.hasPrevious) return;
    navDirectionRef.current = direction;
    nav.onNavigate(direction);
  }, []);

  // Touch: a horizontal swipe pages to the neighbor (the arrow buttons are the
  // pointer affordance; a swipe is the expected phone gesture). The tap the
  // browser synthesizes from that swipe must not also close the overlay, so
  // canceling the `touchend` suppresses it where the browser honours that, and
  // `lastSwipeAtRef` covers the rest.
  //
  // The listener sits on the backdrop while the image and the media player live
  // inside it (only their *clicks* are stopped, so the frame never closes the
  // overlay), so their touches bubble up here and a swipe over the file itself
  // pages just like one over the scrim. Two gestures that are not swipes are
  // turned away up front (see `onBackdropTouchStart`): a drag that starts on the
  // native control bar, which is seeking rather than paging, and a pinch-zoom on
  // the image (two fingers, which can end with a horizontal delta).
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  // When the last swipe paged. A timestamp rather than a "swallow the next
  // click" flag, because that click does not always arrive (the finger lifts
  // outside the window, the swipe also scrolled, or the frame under the finger
  // stops it), and a flag left standing then eats the next genuine tap. This
  // expires on its own, so no gesture can poison a later one.
  const lastSwipeAtRef = useRef(0);
  const onBackdropTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    // Not a swipe: no touch to follow (a synthetic event), a second finger
    // pinch-zooming the image, or a press on a player's control bar, which
    // belongs to the player (scrubbing, volume). The player's picture stays
    // swipeable, since on a phone it is most of the screen.
    if (
      !t ||
      e.touches.length > 1 ||
      isMediaControlsTouch(e.target, t.clientY)
    ) {
      swipeStartRef.current = null;
      return;
    }
    swipeStartRef.current = { x: t.clientX, y: t.clientY };
  }, []);
  const onBackdropTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const start = swipeStartRef.current;
      swipeStartRef.current = null;
      if (!start) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) <= Math.abs(dy)) return;
      lastSwipeAtRef.current = performance.now();
      // Canceling the touchend keeps the browser from synthesizing a click for
      // this gesture at all, which is what every mobile browser we target does.
      e.preventDefault();
      navigate(dx < 0 ? 1 : -1);
    },
    [navigate]
  );

  // Arrow keys page through the neighbors while the overlay is open.
  const hasNavigation = !!previewProp.navigation;
  useEffect(() => {
    if (!hasNavigation) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      // Leave the arrows to a focused media player (they seek there) or any
      // focused text field.
      if (handlesOwnKeys(e.target)) return;
      e.preventDefault();
      navigate(e.key === "ArrowRight" ? 1 : -1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hasNavigation, navigate]);

  // Server error (429, or a 401 whose token refresh failed): replace the
  // preview overlay with the error dialog, showing the server's error localized
  // via the error bundle (isApiException). Dismissing closes the preview.
  if (serverError) {
    return (
      <ExceptionDialog
        isApiException
        title={serverError.title}
        description={serverError.description}
        onDismiss={previewProp.onClose}
      />
    );
  }

  const navigation = previewProp.navigation;
  const isLoadingNeighbor = slots.some((s) => s.state === "pending");

  return (
    <Modal open={true} onClose={previewProp.onClose} closeAfterTransition>
      {/* Full-screen backdrop — clicking anywhere outside the frame dismisses;
          a horizontal swipe pages instead (its trailing tap is swallowed). */}
      <div
        className="fixed inset-0 flex items-center justify-center"
        onClick={() => {
          // The tap a swipe leaves behind pages, it does not dismiss.
          if (
            performance.now() - lastSwipeAtRef.current <
            CLICK_AFTER_SWIPE_MS
          ) {
            return;
          }
          previewProp.onClose();
        }}
        onTouchStart={hasNavigation ? onBackdropTouchStart : undefined}
        onTouchEnd={hasNavigation ? onBackdropTouchEnd : undefined}
      >
        <Tooltip title={t("Close")} placement="left">
          <button
            type="button"
            aria-label={t("Close")}
            // stopPropagation so the click isn't also handled by the backdrop;
            // the button dismisses on its own.
            onClick={(event) => {
              event.stopPropagation();
              previewProp.onClose();
            }}
            className="absolute right-5 top-5 z-10 flex items-center justify-center rounded-full p-2 text-white outline-none transition-colors hover:bg-white/10"
          >
            <CloseIcon sx={{ color: "#ffffff", fontSize: 28 }} />
          </button>
        </Tooltip>
        {navigation?.hasPrevious && (
          <PreviewNavButton
            direction={-1}
            label={t("Previous file")}
            onNavigate={navigate}
          />
        )}
        {navigation?.hasNext && (
          <PreviewNavButton
            direction={1}
            label={t("Next file")}
            onNavigate={navigate}
          />
        )}
        {slots.map((slot) => (
          <PreviewSlot
            key={slot.id}
            slot={slot}
            transferRecipientId={previewProp.transferRecipientId}
            refreshAuth={previewProp.refreshAuth}
            getAuthToken={previewProp.getAuthToken}
            onReady={promoteSlot}
            onServerError={(error) => handleSlotServerError(slot.id, error)}
          />
        ))}
        {/* Neighbor-load indicator: delayed (see index.css) so quick hand-offs
            never flash it; the outgoing file stays visible behind it. */}
        {isLoadingNeighbor && (
          <div className="preview-nav-loading pointer-events-none absolute bottom-8 left-1/2 z-10 -translate-x-1/2">
            <Spinner size={28} color="#ffffff" compact />
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Prev/next arrow on the lightbox backdrop, styled to match the close button. */
function PreviewNavButton({
  direction,
  label,
  onNavigate,
}: {
  direction: 1 | -1;
  label: string;
  onNavigate: (direction: 1 | -1) => void;
}) {
  const Icon = direction === 1 ? ChevronRightIcon : ChevronLeftIcon;
  return (
    <Tooltip title={label} placement="top">
      <button
        type="button"
        aria-label={label}
        // stopPropagation: paging must not fall through to the backdrop-close.
        onClick={(event) => {
          event.stopPropagation();
          onNavigate(direction);
        }}
        onDoubleClick={(event) => event.stopPropagation()}
        className={`absolute top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded-full p-2 text-white outline-none transition-[background-color,transform] duration-150 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/70 active:scale-[0.92] ${
          direction === 1 ? "right-4" : "left-4"
        }`}
      >
        <Icon sx={{ color: "#ffffff", fontSize: 34 }} />
      </button>
    </Tooltip>
  );
}

/**
 * One file's preview: the dark frame plus its {@link FilePreview} content,
 * owning the per-file load phase. The slot's {@link SlotState} drives the
 * paging hand-off (opacity/translate only); it reports up the moment it is
 * displayable so the page can promote it.
 */
function PreviewSlot({
  slot,
  transferRecipientId,
  refreshAuth,
  getAuthToken,
  onReady,
  onServerError,
}: {
  slot: PreviewSlotData;
  transferRecipientId: string;
  refreshAuth?: () => Promise<void>;
  getAuthToken?: () => string | null | undefined;
  onReady: (uid: string) => void;
  onServerError: (error: ServerError) => void;
}) {
  const { entry, state, direction } = slot;
  // Set from the image's intrinsic size on load, so the frame can hug it (no
  // upscaling). Null for non-images and until the image loads.
  const [imageNaturalSize, setImageNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  // Drives the framing: the dark box is shown only for an actual preview
  // ("preview") — never while loading (just the spinner on the scrim) and never
  // behind the inline "no preview" fallback card ("fallback").
  const [phase, setPhase] = useState<"loading" | "preview" | "fallback">(
    "loading"
  );

  const boxStyle = previewBoxStyle(
    entry.mimeType,
    entry.aspectRatio,
    imageNaturalSize
  );

  const slidePx = prefersReducedMotion() ? 0 : NAV_SLIDE_PX;
  const offsetPx =
    state === "pending"
      ? slidePx * direction
      : state === "leaving"
      ? -slidePx * direction
      : 0;

  return (
    <div
      aria-hidden={state !== "shown"}
      // pointer-events-none on the wrapper so clicks in the matting around the
      // frame still reach the backdrop (dismiss); the frame re-enables them.
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
      style={{
        opacity: state === "shown" ? 1 : 0,
        transform: `translateX(${offsetPx}px)`,
        transition: `opacity ${NAV_FADE_MS}ms ease, transform ${NAV_TRANSITION_MS}ms cubic-bezier(0.23, 1, 0.32, 1)`,
        willChange: "transform, opacity",
      }}
    >
      <div
        // The dark frame hugs the content with a neutral backdrop (only
        // visible as matting if a ratio is letterboxed), and ONLY for an
        // actual preview. While loading just the spinner sits on the scrim
        // (no empty black box), and behind the "no preview" fallback card
        // there is no frame at all.
        className={`flex items-center justify-center overflow-hidden ${
          phase === "preview" ? "rounded-lg bg-[#171717] shadow-xl" : ""
        }`}
        style={{
          ...(phase === "preview" ? boxStyle : undefined),
          pointerEvents: state === "shown" ? "auto" : "none",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <FilePreview
          filePreviewUrl={entry.filePreviewUrl}
          fileDownloadUrl={entry.fileDownloadUrl}
          fileUrl={entry.fileUrl}
          mimeType={entry.mimeType}
          objectUid={entry.objectUid}
          fileName={entry.fileName}
          transferRecipientId={transferRecipientId}
          refreshAuth={refreshAuth}
          getAuthToken={getAuthToken}
          // Only the visible slot may play media / own the Space key.
          active={state === "shown"}
          onImageLoad={(width, height) =>
            setImageNaturalSize({ width, height })
          }
          onLoaded={() => {
            setPhase("preview");
            onReady(entry.objectUid);
          }}
          onFallback={() => {
            setPhase("fallback");
            onReady(entry.objectUid);
          }}
          onServerError={onServerError}
        />
      </div>
    </div>
  );
}
